// creator-portal/src/pages/EmailVerification.tsx
// ✅ NEW: Email OTP Verification page - matches iOS EmailVerificationView exactly
// Uses same APIs: POST /api/v1/auth/verify-email-otp and POST /api/v1/auth/resend-verification-otp

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import '../styles/Auth.css';

const EmailVerification: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get email from navigation state (passed from Register page)
    const email = (location.state as { email?: string })?.email || '';

    // OTP state - 6 digits, same as iOS
    const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(''));
    const [focusedField, setFocusedField] = useState<number>(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

    // UI state
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Resend cooldown - 60 seconds, same as iOS
    const [resendCountdown, setResendCountdown] = useState(60);

    // Guard against double-verification (same as iOS hasTriggeredVerification)
    const hasTriggeredVerification = useRef(false);

    // Redirect if no email provided
    useEffect(() => {
        if (!email) {
            navigate('/register', { replace: true });
        }
    }, [email, navigate]);

    // Focus first input on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    // Resend countdown timer - same as iOS timer
    useEffect(() => {
        if (resendCountdown <= 0) return;
        const timer = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCountdown]);

    // Computed: is OTP complete? Same as iOS isOTPComplete
    const isOTPComplete = otpDigits.every((d) => d.length === 1);
    const otpString = otpDigits.join('');

    // ✅ Auto-verify when all 6 digits entered - same as iOS behavior
    const verifyOTP = useCallback(async () => {
        if (!isOTPComplete) return;
        if (isVerifying) return;

        setIsVerifying(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            // POST /api/v1/auth/verify-email-otp  { email, otp }
            const response = await apiService.verifyEmailOTP(email, otpString);

            if (response.success) {
                setSuccessMessage('Email verified successfully! You can now login.');

                // Delay then navigate to login - same as iOS (1.5s delay)
                setTimeout(() => {
                    navigate('/login', {
                        state: {
                            message: 'Email verified successfully! You can now login.',
                        },
                        replace: true,
                    });
                }, 1500);
            } else {
                setErrorMessage(response.message || 'Verification failed');

                // ✅ Reset trigger flag on error so user can retry - same as iOS
                hasTriggeredVerification.current = false;

                // Clear OTP fields on error - same as iOS
                setOtpDigits(Array(6).fill(''));
                setFocusedField(0);
                setTimeout(() => inputRefs.current[0]?.focus(), 50);
            }
        } catch (err: any) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                'Network error. Please try again.';
            setErrorMessage(msg);

            // ✅ Reset trigger flag on error - same as iOS
            hasTriggeredVerification.current = false;

            setOtpDigits(Array(6).fill(''));
            setFocusedField(0);
            setTimeout(() => inputRefs.current[0]?.focus(), 50);
        } finally {
            setIsVerifying(false);
        }
    }, [isOTPComplete, isVerifying, email, otpString, navigate]);

    // ✅ Resend OTP - same as iOS resendOTP()
    const resendOTP = async () => {
        if (resendCountdown > 0 || isResending) return;

        setIsResending(true);
        setErrorMessage(null);

        // ✅ Reset trigger flag when resending - same as iOS
        hasTriggeredVerification.current = false;

        try {
            // POST /api/v1/auth/resend-verification-otp  { email }
            const response = await apiService.resendVerificationOTP(email);

            if (response.success) {
                setSuccessMessage('New code sent to your email.');
                setResendCountdown(60); // 60 seconds cooldown - same as iOS

                setTimeout(() => setSuccessMessage(null), 3000); // Clear after 3s - same as iOS
            } else {
                setErrorMessage(response.message || 'Failed to resend code');
            }
        } catch (err: any) {
            setErrorMessage(
                err.response?.data?.message ||
                err.message ||
                'Network error. Please try again.'
            );
        } finally {
            setIsResending(false);
        }
    };

    // ✅ Handle OTP input - matches iOS handleOTPInput exactly
    const handleOTPInput = (index: number, value: string) => {
        // Handle paste - distribute digits across fields (same as iOS paste handling)
        if (value.length > 1) {
            const digits = value.replace(/\D/g, '').split('');
            if (digits.length >= 6 - index) {
                const newOtp = [...otpDigits];
                for (let i = 0; i < Math.min(digits.length, 6 - index); i++) {
                    newOtp[index + i] = digits[i];
                }
                setOtpDigits(newOtp);
                const nextFocus = Math.min(index + digits.length, 5);
                setFocusedField(nextFocus);
                inputRefs.current[nextFocus]?.focus();

                // ✅ Check and trigger verification ONCE after paste - same as iOS
                const allFilled = newOtp.every((d) => d.length === 1);
                if (allFilled && !hasTriggeredVerification.current && !isVerifying) {
                    hasTriggeredVerification.current = true;
                    // Use setTimeout to let state update first
                    setTimeout(() => {
                        // Re-check with the new otp
                        const otp = newOtp.join('');
                        verifyWithOTP(otp);
                    }, 100);
                }
                return;
            }
            // If not enough digits for full paste, take last character
            value = value.slice(-1);
        }

        // Single digit input
        const digit = value.replace(/\D/g, '').slice(0, 1);
        const newOtp = [...otpDigits];
        newOtp[index] = digit;
        setOtpDigits(newOtp);

        // Auto-advance to next field - same as iOS
        if (digit.length === 1 && index < 5) {
            setFocusedField(index + 1);
            inputRefs.current[index + 1]?.focus();
        }

        // ✅ Auto-verify when all 6 digits entered - but only ONCE (same as iOS)
        const allFilled = newOtp.every((d) => d.length === 1);
        if (allFilled && !hasTriggeredVerification.current && !isVerifying) {
            hasTriggeredVerification.current = true;
            setTimeout(() => {
                const otp = newOtp.join('');
                verifyWithOTP(otp);
            }, 100);
        }
    };

    // Direct verify with specific OTP string (for auto-verify after state update)
    const verifyWithOTP = async (otp: string) => {
        if (otp.length !== 6) return;
        if (isVerifying) return;

        setIsVerifying(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const response = await apiService.verifyEmailOTP(email, otp);

            if (response.success) {
                setSuccessMessage('Email verified successfully! You can now login.');
                setTimeout(() => {
                    navigate('/login', {
                        state: { message: 'Email verified successfully! You can now login.' },
                        replace: true,
                    });
                }, 1500);
            } else {
                setErrorMessage(response.message || 'Verification failed');
                hasTriggeredVerification.current = false;
                setOtpDigits(Array(6).fill(''));
                setFocusedField(0);
                setTimeout(() => inputRefs.current[0]?.focus(), 50);
            }
        } catch (err: any) {
            const msg =
                err.response?.data?.message || err.message || 'Network error. Please try again.';
            setErrorMessage(msg);
            hasTriggeredVerification.current = false;
            setOtpDigits(Array(6).fill(''));
            setFocusedField(0);
            setTimeout(() => inputRefs.current[0]?.focus(), 50);
        } finally {
            setIsVerifying(false);
        }
    };

    // Handle backspace - navigate to previous field
    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            if (otpDigits[index] === '' && index > 0) {
                // Move to previous field
                const newOtp = [...otpDigits];
                newOtp[index - 1] = '';
                setOtpDigits(newOtp);
                setFocusedField(index - 1);
                inputRefs.current[index - 1]?.focus();
            } else {
                // Clear current field
                const newOtp = [...otpDigits];
                newOtp[index] = '';
                setOtpDigits(newOtp);
            }
            e.preventDefault();
        }
    };

    // Handle going back to register
    const handleBack = () => {
        navigate('/register');
    };

    if (!email) return null;

    return (
        <div
            className="
        min-h-screen
        flex items-center justify-center
        bg-gradient-to-br from-[#2b2b2b] via-[#141414] to-black
        text-white
      "
        >
            <div
                className="
          w-full max-w-md
          rounded-2xl
          bg-gradient-to-br from-[#1a1a1a] via-[#111] to-black
          border border-white/10
          shadow-2xl
          p-8
        "
            >
                {/* Brand - same as Login/Register pages */}
                <div className="text-center mb-8">
                    <div className="relative inline-flex items-center">
                        <h1 className="text-2xl font-semibold tracking-widest">VASTAVIK</h1>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">Creator Portal</p>
                </div>

                {/* Mail icon - matching iOS shield icon */}
                <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <svg
                            className="w-8 h-8 text-emerald-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                </div>

                {/* Title - same text as iOS */}
                <div className="text-center mb-6">
                    <h2 className="text-lg font-medium">Verify Your Email</h2>
                    <p className="text-sm text-gray-400 mt-1">We sent a 6-digit code to</p>
                    <p className="text-sm font-semibold text-emerald-400">{email}</p>
                </div>

                {/* OTP Input Fields - 6 boxes, same as iOS */}
                <div className="flex justify-center gap-2.5 mb-5">
                    {otpDigits.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => { inputRefs.current[index] = el; }}
                            type="text"
                            inputMode="numeric"
                            autoComplete={index === 0 ? 'one-time-code' : 'off'}
                            maxLength={6} // Allow paste of full OTP
                            value={digit}
                            onChange={(e) => handleOTPInput(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onFocus={() => setFocusedField(index)}
                            className={`
                w-12 h-14
                rounded-xl
                bg-white/5
                text-center
                text-xl font-bold
                text-white
                border
                ${focusedField === index
                                    ? 'border-emerald-400 ring-2 ring-emerald-400/30'
                                    : digit
                                        ? 'border-emerald-400/50'
                                        : 'border-white/10'
                                }
                focus:outline-none
                transition
                caret-transparent
              `}
                        />
                    ))}
                </div>

                {/* Error message - same style as iOS error view */}
                {errorMessage && (
                    <div
                        className="
              rounded-lg
              bg-rose-500/10
              border border-rose-500/30
              text-rose-400
              text-sm
              px-4 py-2
              mb-4
              flex items-start gap-2
            "
                    >
                        <svg
                            className="w-4 h-4 flex-shrink-0 mt-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <span>{errorMessage}</span>
                    </div>
                )}

                {/* Success message - same style as iOS success view */}
                {successMessage && (
                    <div
                        className="
              rounded-lg
              bg-emerald-500/10
              border border-emerald-500/30
              text-emerald-400
              text-sm
              px-4 py-2
              mb-4
              flex items-start gap-2
            "
                    >
                        <svg
                            className="w-4 h-4 flex-shrink-0 mt-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <span>{successMessage}</span>
                    </div>
                )}

                {/* Verify button - same as iOS verify button */}
                <button
                    onClick={() => {
                        if (!hasTriggeredVerification.current) {
                            hasTriggeredVerification.current = true;
                        }
                        verifyOTP();
                    }}
                    disabled={!isOTPComplete || isVerifying}
                    className="
            w-full
            rounded-xl
            bg-emerald-500
            py-3
            text-black
            font-medium
            hover:bg-emerald-400
            transition
            disabled:opacity-50
            disabled:cursor-not-allowed
            mb-4
            flex items-center justify-center gap-2
          "
                >
                    {isVerifying && (
                        <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                        </svg>
                    )}
                    {isVerifying ? 'Verifying...' : 'Verify Email'}
                </button>

                {/* Resend code section - same as iOS */}
                <div className="text-center space-y-3">
                    <p className="text-xs text-gray-400">Didn't receive the code?</p>
                    <button
                        onClick={resendOTP}
                        disabled={resendCountdown > 0 || isResending}
                        className={`
              text-sm font-medium
              ${resendCountdown > 0
                                ? 'text-gray-500 cursor-not-allowed'
                                : 'text-emerald-400 hover:text-emerald-300 hover:underline'
                            }
              transition
              disabled:cursor-not-allowed
            `}
                    >
                        {isResending
                            ? 'Sending...'
                            : resendCountdown > 0
                                ? `Resend in ${resendCountdown}s`
                                : 'Resend Code'}
                    </button>
                </div>

                {/* Back button - same as iOS onBack */}
                <div className="text-center mt-5">
                    <button
                        onClick={handleBack}
                        className="text-sm text-gray-400 hover:text-gray-300 flex items-center justify-center gap-1 mx-auto"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Back
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;